import { useTheme } from '../../../../../context/ThemeContext';

// Chart colors are picked per mode, not flipped automatically: a hue that clears
// contrast on white does not necessarily clear it on the dark surface. Each set
// below was run through the palette validator (lightness band, chroma floor,
// colorblind separation, normal-vision separation, contrast vs surface) and
// passes in both modes.
//
// The status trio is deliberately green/blue/red rather than green/orange/red:
// refunded-red against pending-orange measures ΔE 10.8 for normal vision, under
// the 15 floor, so the two states are hard to tell apart even with full color
// vision. Blue doubles as this app's info color, so awaiting-payment reads right.
const LIGHT = {
  series: '#2a78d6',      // blue, sequential + categorical slot 1
  seriesSoft: '#9ec5f4',
  alt: '#eb6834',         // orange, categorical slot 2
  collected: '#0ca30c',
  awaiting: '#2a78d6',
  lost: '#d03b3b',
  grid: '#e5e7eb',
  axis: '#6b7280',
  surface: '#ffffff',
  track: '#f1f5f9',
};

const DARK = {
  series: '#3987e5',
  seriesSoft: '#1c5cab',
  alt: '#d95926',
  collected: '#0ca30c',
  awaiting: '#3987e5',
  lost: '#d03b3b',
  grid: '#374151',
  axis: '#9ca3af',
  surface: '#111827',
  track: '#1f2937',
};

// Pipeline stages are ORDERED, not distinct identities, so they take one hue
// stepped light to dark rather than four separate colors. Validated as an ordinal
// ramp in both modes: monotone lightness, visible step gaps, and the light end
// still clears the surface. `returned` is not a stage on that path, it is an
// exception, so it wears the warning color instead of a step.
const FUNNEL_LIGHT = ['#86b6ef', '#5598e7', '#2a78d6', '#184f95'];
const FUNNEL_DARK = ['#9ec5f4', '#6da7ec', '#3987e5', '#1c5cab'];

export function useChartColors() {
  const { theme } = useTheme() || {};
  const dark = theme === 'dark';
  return { ...(dark ? DARK : LIGHT), funnel: dark ? FUNNEL_DARK : FUNNEL_LIGHT };
}

export const money = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
export const moneyExact = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

// A bucket key is a plain date string from the API ("2026-07" or "2026-07-27").
// Parsed as UTC noon so a timezone offset can never shift the label a day.
export function bucketLabel(bucket) {
  if (!bucket) return '';
  const [y, m, d] = bucket.split('-');
  const date = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d || 1), 12));
  return d
    ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : date.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
}

// Null when there is nothing to compare against, so the caller renders no delta
// rather than a 0% that looks like real flatness.
export function pctChange(current, prior) {
  if (prior == null) return null;
  if (prior === 0) return current > 0 ? Infinity : null;
  return ((current - prior) / prior) * 100;
}
